/**
 * AppNavLink — the app chrome's navigation entry, rendered as a REAL anchor.
 *
 * The sidebar rendered every entry as a button element calling `navigate()`, which
 * works for a plain click and for nothing else: the browser's own middle-click,
 * Cmd/Ctrl-click and right-click -> "Open link in new tab" all read an `href`, and
 * a button has none. So every side-menu entry in the app was the one kind of link a
 * user could not open in a second tab — and there is no way to synthesise the
 * context menu from JavaScript, which is why this is an element change and not a
 * handler change. (Spelled in prose, not as a tag: the element-substitution gate
 * reads tags out of this file and a literal one here reads as a raw control.)
 *
 * It keeps the SPA behaviour for the plain left click only. A MODIFIED click
 * (Cmd/Ctrl/Shift/Alt, or any non-primary button) and a link carrying its own
 * `target` are handed to the browser untouched — that hand-off IS the feature.
 *
 * The in-app navigation goes through `useGuardedNavigate()`, per
 * `.claude/rules/rules-ui-navigation.md` § "An app-chrome navigator takes
 * useGuardedNavigate()". A dirty form now ALSO sees the click from the other
 * side: `useUnsavedChangesWarning`'s capture-phase anchor interceptor listens on
 * `document`, ahead of React's own delegated listener, and stops the event — so
 * `handleClick` is simply never reached in that case and there is still exactly
 * one prompt, one copy and one `navigate()`.
 */

import { forwardRef } from 'react';
import type { AnchorHTMLAttributes, MouseEvent as ReactMouseEvent } from 'react';

import { useGuardedNavigate } from '../../hooks/useGuardedNavigate';

export interface AppNavLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  /**
   * The destination, rendered verbatim as the anchor's `href`. Normally an in-app
   * path; an EXTERNAL URL is fine when paired with `target` (the recruiting Jobs
   * Portal), which routes the whole click to the browser rather than to the router.
   */
  to: string;
  /**
   * Side effect to run once an in-app navigation has been started — closing the
   * mobile drawer, dismissing a popover. NOT called when the browser handles the
   * click (a new tab), because nothing in this window navigated.
   */
  onNavigate?: () => void;
}

export const AppNavLink = forwardRef<HTMLAnchorElement, AppNavLinkProps>(function AppNavLink(
  { to, onNavigate, onClick, target, children, ...anchorProps },
  ref,
) {
  const navigate = useGuardedNavigate();

  const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    // Leave these to the browser — that is what makes the native gestures work.
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (target && target !== '_self') return;

    event.preventDefault();
    navigate(to);
    onNavigate?.();
  };

  return (
    <a ref={ref} href={to} target={target} onClick={handleClick} {...anchorProps}>
      {children}
    </a>
  );
});
